import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { neetPremiumSeries, neetUltimatePremium, findSeriesById } from '../../data/testSeriesData';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, collection, query, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { calculateTestStatus, parsePurchaseDate } from '../../utils/testStatusHelper';
import Footer from '../Footer';
import SocialSidebar from '../../components/SocialSideBar';
import styles from './NeetPremiumPage.module.css';
// Dynamically require all images in the images folder
const neetImages = {
  neet_ultimate_combo: require('./images/neet_combo.jpg'),
  neet_c11_chap: require('./images/neet_class11.jpg'),
  neet_c12_chap: require('./images/neet_class12.jpg'),
  neet_c11_full: require('./images/neet_full11.jpg'),
  neet_c12_full: require('./images/neet_full12.jpg'),
  neet_11_12_full: require('./images/neet_full11_12.jpg'),
  neet_aits: require('./images/neet_aits.jpg'),
};

const NeetPremiumPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [testStatuses, setTestStatuses] = useState({}); // {testId: {status: 'Active'|'Expired'|'Not Purchased', expiryDate: Date}}
  const [filterType, setFilterType] = useState('all'); // all, chapterwise, full-length, aits, all-india

  const allTests = [...neetUltimatePremium, ...neetPremiumSeries];

  useEffect(() => {
    const loadTestStatuses = async () => {
      if (!currentUser) {
        // If not logged in, all tests are 'Not Purchased'
        const statuses = {};
        allTests.forEach(test => {
          statuses[test.id] = { status: 'Not Purchased' };
        });
        setTestStatuses(statuses);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        let purchasedCourseIds = [];
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          purchasedCourseIds = userData.purchasedCourses || [];
          console.log('🔍 NEET Premium - User purchased courses:', purchasedCourseIds);
        }
        
        // FALLBACK: If purchasedCourses array is empty, fetch from transactions collection
        if (purchasedCourseIds.length === 0) {
          console.log('⚠️ purchasedCourses array empty, fetching from transactions...');
          const transactionsRef = collection(db, 'transactions');
          const q = query(transactionsRef);
          const querySnapshot = await getDocs(q);
          
          querySnapshot.forEach((doc) => {
            const tx = doc.data();
            if (tx.userId === currentUser.uid && tx.status === 'success' && tx.courseId) {
              if (!purchasedCourseIds.includes(tx.courseId)) {
                purchasedCourseIds.push(tx.courseId);
              }
            }
          });
          
          console.log('✅ Fetched from transactions:', purchasedCourseIds);
          
          // Update user document with fetched courses for future
          if (purchasedCourseIds.length > 0) {
            try {
              await updateDoc(userDocRef, { purchasedCourses: purchasedCourseIds });
              console.log('✅ Updated user document with purchasedCourses');
            } catch (updateError) {
              console.warn('Failed to update user document:', updateError);
            }
          }
        }

        const statuses = {};
        const activeComboIds = []; // Track active combos
        
        // First pass: check all tests including combos
        for (const test of allTests) {
          console.log(`🔍 Checking test: ${test.id}`, {
            inPurchased: purchasedCourseIds.includes(test.id),
            testId: test.id
          });
          
          if (!purchasedCourseIds.includes(test.id)) {
            statuses[test.id] = { status: 'Not Purchased' };
            continue;
          }

          // Fetch transaction to get purchase date and calculate expiry
          const txDocId = `${currentUser.uid}_${test.id}`;
          console.log(`📄 Fetching transaction: ${txDocId}`);
          const txDocSnap = await getDoc(doc(collection(db, 'transactions'), txDocId));
          
          if (txDocSnap.exists()) {
            const tx = txDocSnap.data();
            const purchasedDate = parsePurchaseDate(tx);
            const validityYears = test.ValidityYears || 1;
            const { status, expiryDate } = calculateTestStatus(purchasedDate, validityYears);
            
            console.log(`✅ Transaction found for ${test.id}:`, {
              status,
              purchasedDate,
              expiryDate,
              amount: tx.amount
            });
            
            statuses[test.id] = { status, expiryDate, purchasedDate, amount: tx.amount };
            
            // If this is an active combo, track it
            if (status === 'Active' && test.Contains_ids && test.Contains_ids.length > 0) {
              activeComboIds.push(test.id);
            }
          } else {
            // Fallback: Use user creation date if transaction is missing
            console.warn(`⚠️ No transaction found for ${test.id}, using fallback date`);
            const userData = userDocSnap.data();
            const fallbackDate = userData?.createdAt?.toDate() || new Date();
            const validityYears = test.ValidityYears || 1;
            const { status, expiryDate } = calculateTestStatus(fallbackDate, validityYears);
            statuses[test.id] = { status, expiryDate, purchasedDate: fallbackDate, amount: 0 };
            
            // If this is an active combo, track it
            if (status === 'Active' && test.Contains_ids && test.Contains_ids.length > 0) {
              activeComboIds.push(test.id);
            }
          }
        }

        // Second pass: mark individual tests as Active if their combo is active
        for (const test of allTests) {
          // Skip if already purchased individually
          if (statuses[test.id].status !== 'Not Purchased') continue;
          
          // Check if this test is part of any active combo
          for (const comboId of activeComboIds) {
            const combo = allTests.find(t => t.id === comboId);
            if (combo && combo.Contains_ids && combo.Contains_ids.includes(test.id)) {
              statuses[test.id] = { 
                status: 'Active', 
                purchasedViaCombo: true,
                comboId: comboId,
                expiryDate: statuses[comboId].expiryDate,
                purchasedDate: statuses[comboId].purchasedDate,
                amount: statuses[comboId].amount
              };
              break; // Stop checking other combos
            }
          }
        }

        setTestStatuses(statuses);
      } catch (error) {
        console.error('Error loading test statuses:', error);
        // On error, mark all as 'Not Purchased'
        const statuses = {};
        allTests.forEach(test => {
          statuses[test.id] = { status: 'Not Purchased' };
        });
        setTestStatuses(statuses);
      }
    };

    loadTestStatuses();
  }, [currentUser]);

  // Check if test is part of combo pack
  const isInCombo = (testId) => {
    return neetUltimatePremium.some(combo => 
      combo.Contains_ids && combo.Contains_ids.includes(testId)
    );
  };

  // Get included tests for combo packs
  const getIncludedTests = (combo) => {
    if (!combo.Contains_ids) return [];
    return combo.Contains_ids.map(id => {
      const test = neetPremiumSeries.find(t => t.id === id);
      return test ? test.title : null;
    }).filter(Boolean);
  };

  // Format test metadata with proper units
  const formatMetadata = (test) => {
    // For combo packs (TestTime = 0 means mixed/not applicable)
    if (test.TestTime === 0) {
      return `${test.NumberOfTests}+ Tests | Full Access`;
    }
    
    // For regular tests
    const tests = test.NumberOfTests === 1 ? '1 Test' : `${test.NumberOfTests} Tests`;
    const time = `${test.TestTime} min each`;
    return `${tests} | ${time}`;
  };

  // Calculate savings percentage
  const calculateSavings = (oldPrice, newPrice) => {
    if (!oldPrice) return 0;
    const oldVal = parseInt(oldPrice.replace(/[^0-9]/g, ''));
    const newVal = parseInt(newPrice.replace(/[^0-9]/g, ''));
    if (!oldVal || !newVal) return 0;
    return Math.round(((oldVal - newVal) / oldVal) * 100);
  };

  // Get unique includes points for each test
  const getTestIncludes = (testId) => {
    const includesMap = {
      'neet_c11_chap': [
        '15 Chapter Tests: Physics',
        '15 Chapter Tests: Chemistry',
        '15 Chapter Tests: Biology'
      ],
      'neet_c12_chap': [
        '15 Chapter Tests: Physics',
        '15 Chapter Tests: Chemistry',
        '15 Chapter Tests: Biology'
      ],
      'neet_c11_full': [
        '10 Full-Length Mock Tests',
        '180 Questions per Test',
        '200 Minutes Test Duration'
      ],
      'neet_c12_full': [
        '10 Full-Length Mock Tests',
        '180 Questions per Test',
        '200 Minutes Test Duration'
      ],
      'neet_11_12_full': [
        '12 Full-Length Mock Tests',
        '200 Questions per Test',
        'Complete Class 11+12 Coverage'
      ],
      'neet_aits': [
        '15 Chapter-wise Tests',
        '6 Mixed-Topic Tests',
        '6 Full-Length Tests'
      ]
    };
    return includesMap[testId] || [];
  };

  // Filter tests based on selected type
  const filteredTests = allTests.filter(test => {
    if (filterType === 'all') return true;
    
    const title = test.title.toLowerCase();
    switch(filterType) {
      case 'chapterwise':
        return title.includes('chapter');
      case 'full-length':
        return title.includes('full') || title.includes('mock');
      case 'all-india':
        return title.includes('all india') || title.includes('all-india');
      default:
        return true;
    }
  });

  return (
    <>
      <div className={styles.pageContainer}>
        <SocialSidebar />
        
        <main className={styles.mainContent}>
          <div className={styles.headerSection}>
            <h1 className={styles.pageTitle}>NEET Premium Test Categories</h1>
          
          <div className={styles.filterContainer}>
            <label htmlFor="test-filter" className={styles.filterLabel}>🔍 Filter:</label>
            <select 
              id="test-filter"
              className={styles.filterDropdown}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Tests</option>
              <option value="chapterwise">Chapterwise</option>
              <option value="full-length">Full Length</option>
              <option value="all-india">All India Test Series</option>
            </select>
          </div>
        </div>
        
        <div className={styles.testsGrid}>
          {filteredTests.map((test) => {
            const inCombo = isInCombo(test.id);
            const includedTests = test.Contains_ids ? getIncludedTests(test) : [];
            const testStatus = testStatuses[test.id] || { status: 'Not Purchased' };
            const isCombo = test.Contains_ids && test.Contains_ids.length > 0;
            
            // Debug logging
            if (test.id === 'neet_ultimate_premium') {
              console.log('Test Status for', test.id, ':', testStatus);
            }
            
            return (
              <div key={test.id} className={styles.testCard} data-is-combo={isCombo}>
                <div className={styles.cardHeader}>
                  <span className={styles.testBadge}>📚 TEST SERIES</span>
                  <span className={`${styles.statusBadge} ${
                    testStatus.status === 'Active' ? styles.active : 
                    testStatus.status === 'Expired' ? styles.expired : 
                    styles.notPurchased
                  }`}>
                    ● {testStatus.status}{testStatus.purchasedViaCombo ? ' (via Combo)' : ''}
                  </span>
                </div>
                
                <div className={styles.cardBody}>
                  <h2 className={styles.testTitle}>{test.title}</h2>
                  
                  <div className={styles.testMeta}>
                    {formatMetadata(test)}
                  </div>
                  {/* Insert image after meta */}
                  {neetImages[test.id] && (
                    <div className={styles.testImageWrapper}>
                      <img
                        src={neetImages[test.id]}
                        alt={test.title}
                        className={styles.testImage}
                        loading="lazy"
                      />
                    </div>
                  )}
                  
                  {/* Includes section removed as per user request */}
                  
                  {testStatus.status !== 'Not Purchased' ? (
                    <div className={styles.purchasedInfo}>
                      <div className={styles.compactRow}>
                        <div className={styles.compactItem}>
                          <span className={styles.compactLabel}>Purchased</span>
                          <span className={styles.compactValue}>
                            {testStatus.purchasedDate ? testStatus.purchasedDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                          </span>
                        </div>
                        <div className={styles.compactItem}>
                          <span className={styles.compactLabel}>Expires</span>
                          <span className={styles.compactValue}>
                            {testStatus.expiryDate ? testStatus.expiryDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className={styles.compactRow}>
                        <div className={styles.compactItem}>
                          <span className={styles.compactLabel}>Amount</span>
                          <span className={styles.compactPrice}>
                            ₹{testStatus.amount || test.price.replace(/[^0-9]/g, '')}
                          </span>
                        </div>
                        <div className={styles.compactItem}>
                          <span className={styles.compactLabel}>Validity</span>
                          <div className={styles.validityTagCompact}>
                            {testStatus.purchasedViaCombo && (
                              <span className={styles.strikeValidity}>{test.ValidityYears}Y</span>
                            )}
                            {testStatus.purchasedViaCombo ? ' → 2Y' : `${test.ValidityYears}Y`}
                            {testStatus.purchasedViaCombo && <span className={styles.viaCombo}> via Combo</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.pricingCompact}>
                      <div className={styles.priceLine}>
                        <span className={styles.priceLabel}>Price:</span>
                        {test.oldPrice && (
                          <span className={styles.oldPriceCompact}>{test.oldPrice}</span>
                        )}
                        <span className={styles.priceCompact}>{test.price}</span>
                        {test.oldPrice && (
                          <span className={styles.discountCompact}>
                            <span className={styles.discountAnim}>{calculateSavings(test.oldPrice, test.price)}% OFF</span>
                          </span>
                        )}
                      </div>
                      <div className={styles.validityLine}>
                        <span className={styles.validityLabel}>Validity:</span>
                        <span className={styles.validityValue}>
                          {test.combo ? '2Year (Combo)' : `${test.ValidityYears}Year`}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <button
                    className={testStatus.status === 'Active' ? styles.btnActive : styles.btnExplore}
                    onClick={() => {
                      if (testStatus.status === 'Active') {
                        navigate(`/purchased-tests/${test.id}`, {
                          state: testStatus.purchasedViaCombo
                            ? { comboInfo: { id: testStatus.comboId, expiryDate: testStatus.expiryDate } }
                            : null
                        });
                      } else {
                        navigate(test.link);
                      }
                    }}
                  >
                    {testStatus.status === 'Active' ? 'View Tests →' : 'Explore Now'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </main>
      </div>

      <Footer />
    </>
  );
};

export default NeetPremiumPage;